/// Rules for the CV file on the careers form. Shared by the form (instant feedback) and the API
/// routes (the real check). Lead Engine then downloads the uploaded file and checks it again by its
/// bytes before linking it to the application, so nothing here is the last line of defence.

export const CV_MAX_BYTES = 5 * 1024 * 1024;
export const CV_ACCEPT =
  ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export type CvKind = "pdf" | "doc" | "docx";

export const CV_CONTENT_TYPES: Record<CvKind, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

/** The one name the browser may ask to upload to. Blob adds a random suffix to it. */
export function cvRequestPath(kind: CvKind): string {
  return `applicant-cvs/uploads/cv.${kind}`;
}

const REQUEST_PATH = /^applicant-cvs\/uploads\/cv\.(pdf|doc|docx)$/;
const STORED_PATH = /^applicant-cvs\/uploads\/cv-[A-Za-z0-9]{8,64}\.(pdf|doc|docx)$/;

export function isCvRequestPath(pathname: unknown): boolean {
  return typeof pathname === "string" && REQUEST_PATH.test(pathname);
}

export function isCvStoredPath(pathname: unknown): boolean {
  return typeof pathname === "string" && STORED_PATH.test(pathname);
}

export function cvKindFromName(name: string): CvKind | null {
  const ext = name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  return ext === "pdf" || ext === "doc" || ext === "docx" ? ext : null;
}

/** First bytes of each real format. A file renamed to .pdf fails this. */
export function cvKindFromBytes(bytes: Uint8Array): CvKind | null {
  const starts = (sig: number[]) => sig.every((b, i) => bytes[i] === b);
  if (starts([0x25, 0x50, 0x44, 0x46, 0x2d])) return "pdf";
  if (starts([0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) return "doc";
  if (starts([0x50, 0x4b, 0x03, 0x04])) return "docx";
  return null;
}

export type CvFileProblem = "too_big" | "empty" | "wrong_type";

/** What the applicant reads for each problem. Plain, and says what to do next. */
export const CV_PROBLEM_MESSAGES: Record<CvFileProblem | "mismatch" | "missing" | "failed" | "unavailable", string> = {
  too_big: "That file is over 5 MB. Save a smaller PDF, or paste a link to your CV instead.",
  empty: "That file looks empty. Please choose your CV again.",
  wrong_type: "Please upload a PDF or Word document (PDF, DOC or DOCX).",
  mismatch: "That file does not look like the type its name says. Save it again as a PDF and upload that.",
  missing: "Your CV did not finish uploading. Please choose it again.",
  failed: "Your CV did not upload. Check your connection and try again, or paste a link instead.",
  unavailable: "CV uploads are not working right now. Paste a link to your CV instead.",
};

/** Checks that need only the name and size. */
export function cvFileProblem(file: { name: string; size: number }): CvFileProblem | null {
  if (file.size === 0) return "empty";
  if (!cvKindFromName(file.name)) return "wrong_type";
  if (file.size > CV_MAX_BYTES) return "too_big";
  return null;
}

/** Map Lead Engine's refusal reason (from its content check) to the applicant's message. */
export function cvRefusalMessage(reason: unknown): string {
  switch (reason) {
    case "too_big":
      return CV_PROBLEM_MESSAGES.too_big;
    case "empty":
      return CV_PROBLEM_MESSAGES.empty;
    case "mismatch":
      return CV_PROBLEM_MESSAGES.mismatch;
    case "missing":
      return CV_PROBLEM_MESSAGES.missing;
    case "not_configured":
      return CV_PROBLEM_MESSAGES.unavailable;
    default:
      return CV_PROBLEM_MESSAGES.wrong_type;
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
